import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import { useNavigate } from 'react-router-dom'
import savedGameApi, { SavedGame } from '../../data/api/saved-game-api'
import userApi from '../../data/api/user/user-api'
import { saveLoggedInUser } from '../../data/auth/auth-storage'
import {
  clearLoggedInUser,
  getLoggedInUser,
} from '../../data/auth/auth-storage'

const UserProfilePage = (): React.JSX.Element => {
  const navigate = useNavigate()
  const loggedInUser = getLoggedInUser()
  const [libraryGames, setLibraryGames] = useState<SavedGame[]>([])
  const [loading, setLoading] = useState(true)
  const [allowAdultContent, setAllowAdultContent] = useState(loggedInUser?.allowAdultContent ?? false)
  const [error, setError] = useState('')

  useEffect(() => {
    const loadProfileGames = async () => {
      if (!loggedInUser?.id) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError('')

        const games = await savedGameApi.getLibrary(loggedInUser.id)
        setLibraryGames(games)
      } catch (err) {
        console.error(err)
        setError('Failed to load your game information.')
      } finally {
        setLoading(false)
      }
    }

    loadProfileGames()
  }, [loggedInUser?.id])

  const favoriteGames = useMemo(
    () => libraryGames.filter((game) => game.isFavorite),
    [libraryGames]
  )

  const gameStats = useMemo(() => {
    const countStatus = (status: string) =>
      libraryGames.filter((game) => game.status === status).length

    return [
      { label: 'Current Library', value: libraryGames.length },
      { label: 'Playing', value: countStatus('Playing') },
      { label: 'Completed', value: countStatus('Completed') },
      { label: 'Backlog', value: countStatus('Backlog') },
      { label: 'Wishlist', value: countStatus('Wishlist') },
    ]
  }, [libraryGames])

      const cardStyle = {
      background:
        'linear-gradient(180deg, rgba(36,58,88,0.96), rgba(24,40,64,0.96))',
      border: '1px solid rgba(148,163,184,0.35)',
      borderRadius: 3,
      color: '#f8fbff',
      boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
    }

  const handleLogout = () => {
    clearLoggedInUser()
    navigate('/Login')
  }

  const handleRemoveFavorite = async (game: SavedGame) => {
    if (!game.id) return

    try {
      const updatedGame = await savedGameApi.updateFavorite(game.id, false)

      setLibraryGames((games) =>
        games.map((item) =>
          item.id === game.id ? updatedGame : item
        )
      )
    } catch (err) {
      console.error(err)
      setError('Failed to remove the game from your favorites.')
    }
  }

        const handleAdultContentToggle = async (
          event: React.ChangeEvent<HTMLInputElement>
        ) => {
          if (!loggedInUser) return

          const checked = event.target.checked

          setAllowAdultContent(checked)
          setError('')

          try {
            const updatedUser =
              await userApi.updateAdultContentPreference(
                loggedInUser.id,
                checked
              )

            saveLoggedInUser(updatedUser)
          } catch (err) {
            console.error(err)

            setAllowAdultContent(!checked)
            setError('Failed to update mature content preference.')
          }
        }

  if (!loggedInUser) {
    return (
      <Box
        sx={{
          background:
            'linear-gradient(180deg, #0b1220 0%, #0f1724 100%)',
          minHeight: '100vh',
          width: '100vw',
          marginLeft: 'calc(50% - 50vw)',
          p: 4,
          color: '#f8fbff',
        }}
      >
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
          >
            User Profile
          </Typography>

          <Typography
            variant="body1"
            sx={{ mb: 3 }}
          >
            Please log in to view your profile.
          </Typography>

          <Button
            variant="contained"
            onClick={() => navigate('/Login')}
            sx={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#2563eb',
              },
            }}
          >
            Go to Login
          </Button>
        </Box>
      </Box>
    )
  }

  const fullName =
    `${loggedInUser.firstName} ${loggedInUser.lastName}`.trim()

  const initials =
    `${loggedInUser.firstName?.[0] ?? ''}${
      loggedInUser.lastName?.[0] ?? ''
    }`.toUpperCase()

  return (
    <Box
      sx={{
        background:
          'linear-gradient(180deg, #0b1220 0%, #0f1724 100%)',
        minHeight: '100vh',
        width: '100vw',
        marginLeft: 'calc(50% - 50vw)',
        p: 4,
        color: '#f8fbff',
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ color: '#f8fbff' }}
        >
          User Profile
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mb: 3, flexWrap: 'wrap' }}
        >
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#2563eb',
              },
            }}
          >
            Home
          </Button>

          <Button
            variant="contained"
            onClick={() => navigate('/Games')}
            sx={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#2563eb',
              },
            }}
          >
            Games
          </Button>

          <Button
            variant="contained"
            onClick={() => navigate('/Library')}
            sx={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#2563eb',
              },
            }}
          >
            Game Library
          </Button>

          <Button
            variant="contained"
            onClick={handleLogout}
            sx={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#2563eb',
              },
            }}
          >
            Logout
          </Button>
        </Stack>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        )}

                <Card sx={{ ...cardStyle, mb: 4 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              alignItems="center"
            >
              <Avatar
                sx={{
                  width: 96,
                  height: 96,
                  fontSize: 36,
                  bgcolor: '#3b82f6',
                  color: '#ffffff',
                }}
              >
                {initials}
              </Avatar>

              <Box>
                <Typography
                  variant="h4"
                  component="h2"
                >
                  {fullName}
                </Typography>

                <Typography
                  variant="body1"
                  color="'#c2cedd"
                >
                  {loggedInUser.email}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{ mt: 1 }}
                >
                  Track your library, current progress, and favorite games in
                  one place.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ ...cardStyle, mb: 4 }}>
          <CardContent>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
            >
              Preferences
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={allowAdultContent}
                  onChange={handleAdultContentToggle}
                />
              }
              label="Allow Mature (18+) Content"
            />

            <Typography
              variant="body2"
              color="'#c2cedd"
            >
              When disabled, mature-rated games will be hidden from featured
              games and search results.
            </Typography>
          </CardContent>
        </Card>

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 6,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid
            container
            spacing={3}
          >
            <Grid size={{ xs: 12, md: 7 }}>
              <Card
                sx={{...cardStyle, height: '100%',}}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    component="h2"
                    gutterBottom
                  >
                    Favorite Games
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  {favoriteGames.length === 0 ? (
                    <Box>
                      <Typography
                        color="'#c2cedd"
                        sx={{ mb: 2 }}
                      >
                        You have not selected any favorite games yet.
                      </Typography>

                      <Button
                        variant="contained"
                        onClick={() => navigate('/Library')}
                        sx={{
                          backgroundColor: '#3b82f6',
                          color: '#ffffff',
                          fontWeight: 'bold',
                          '&:hover': {
                            backgroundColor: '#2563eb',
                          },
                        }}
                      >
                        Choose Favorites from Library
                      </Button>
                    </Box>
                  ) : (
                    <Grid
                      container
                      spacing={2}
                    >
                      {favoriteGames.map((game) => (
                        <Grid
                          size={{ xs: 12, sm: 6 }}
                          key={game.id}
                        >
                          <Card
                            sx={{ background: 'linear-gradient(180deg, rgba(29,46,69,0.96), rgba(22,36,56,0.96))', border: '1px solid rgba(148,163,184,0.25)', color: '#f8fbff', }}
                          >
                            {game.imageUrl && (
                              <CardMedia
                                component="img"
                                height="120"
                                image={game.imageUrl}
                                alt={game.title}
                              />
                            )}

                            <CardContent>
                              <Typography
                                variant="h6"
                                noWrap
                              >
                                {game.title}
                              </Typography>

                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleRemoveFavorite(game)}
                                sx={{
                                  mt: 1,
                                  backgroundColor: '#3b82f6',
                                  color: '#ffffff',
                                  fontWeight: 'bold',
                                  '&:hover': {
                                    backgroundColor: '#2563eb',
                                  },
                                }}
                              >
                                Remove Favorite
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card
                sx={{...cardStyle, height: '100%', }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    component="h2"
                    gutterBottom
                  >
                    Game Statistics
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <Grid
                    container
                    spacing={2}
                  >
                    {gameStats.map((stat) => (
                      <Grid
                        size={{ xs: 6 }}
                        key={stat.label}
                      >
                        <Box
                          sx={{
                            textAlign: 'center',
                            p: 1,
                          }}
                        >
                          <Typography
                            variant="h4"
                            component="p"
                          >
                            {stat.value}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="'#c2cedd"
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  )
}

export default UserProfilePage